import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "../icons";

type Tone = "ok" | "error";
interface ToastState {
  text: string;
  tone: Tone;
  id: number;
}

const ToastContext = createContext<(text: string, tone?: Tone) => void>(() => {});

/** Kurze Rückmeldungen, für Screenreader per aria-live angekündigt. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | null>(null);
  const show = useCallback((text: string, tone: Tone = "ok") => {
    setToast({ text, tone, id: Date.now() });
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), tone === "error" ? 6000 : 3600);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div
        className={`toast${toast ? " is-on" : ""}${toast?.tone === "error" ? " is-error" : ""}`}
        role="status"
        aria-live="polite"
      >
        {toast && (
          <>
            <Icon name={toast.tone === "error" ? "alert" : "check"} />
            <span>{toast.text}</span>
          </>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

export function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="form-error" role="alert">
      <Icon name="alert" /> {error}
    </p>
  );
}

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: string;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} />
      </span>
      <strong>{title}</strong>
      <p>{text}</p>
      {action}
    </div>
  );
}

/**
 * Bestätigung vor folgenreichen Schritten. Fokus startet auf „Abbrechen“, Esc bricht ab, Tab bleibt im
 * Dialog, danach kehrt der Fokus zum auslösenden Element zurück. Der Dialog hängt am `body`, weil Panels
 * mit `backdrop-filter` sonst den Bezugsrahmen für `position: fixed` bilden.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const textId = useId();
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLButtonElement>("[data-cancel]")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
      if (e.key === "Tab" && ref.current) {
        const f = ref.current.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [onCancel]);
  return createPortal(
    <div className="confirm-wrap" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div
        className="confirm panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={textId}
        ref={ref}
      >
        <h2 id={titleId}>
          <Icon name="alert" /> {title}
        </h2>
        <div id={textId} className="confirm-text">
          {children}
        </div>
        <div className="confirm-actions">
          <button type="button" className="btn" data-cancel onClick={onCancel}>
            <span className="btn-label">Abbrechen</span>
          </button>
          <button type="button" className="btn btn--danger" onClick={onConfirm} disabled={busy}>
            <span className="btn-label">{busy ? "Wird abgeschaltet …" : confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
